<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch, type Component, type VNode } from 'vue';
import { SparkleIcon } from '@blpde/nx-icons';
import { useNxPaneState } from '../Settings/settingsSource';

export interface NxFabAction {
  key: string;
  label: string;
  icon: Component;
  disabled?: boolean;
  /** Simple count badge; the `badge` slot overrides rendering entirely. */
  badge?: number | string;
}

export type NxFabPosition = 'bottom-right' | 'bottom-left' | { x: number; y: number };

const props = withDefaults(
  defineProps<{
    actions: NxFabAction[];
    /** Main button icon; defaults to a sparkle. */
    icon?: Component;
    /** aria-label of the main button (required — the FAB has no visible text). */
    label: string;
    /** Corner keyword or a {x, y} px offset from the viewport's top-left corner. */
    position?: NxFabPosition;
    draggable?: boolean;
    /**
     * Remember the dragged position in the user's settings
     * (`shell.panes.<storageKey>`, the kit's settings contract, I-2510); a
     * stored position wins over `position`.
     */
    storageKey?: string;
    /** 'single' fires select(actions[0].key) directly — no menu. */
    triggerMode?: 'menu' | 'single';
    /**
     * Count badge on the main button — unread messages on a chat FAB. `0`, `''`
     * and `undefined` show nothing. The count joins the button's accessible name
     * („Firmenchat (3)"), since the badge itself is only painted.
     */
    badge?: number | string;
    /**
     * Shows `label` as a tag beside the main button while it is hovered or
     * keyboard-focused — for a FAB whose icon alone does not say what it opens.
     * The tag opens towards the middle of the screen, never off its edge.
     */
    hoverLabel?: boolean;
  }>(),
  {
    icon: undefined,
    position: 'bottom-right',
    draggable: true,
    storageKey: '',
    triggerMode: 'menu',
    badge: undefined,
    hoverLabel: false,
  }
);

const open = defineModel<boolean>('open', { default: false });
const emit = defineEmits<{
  select: [key: string];
  moved: [pos: { x: number; y: number }];
}>();
defineSlots<{
  icon?: () => VNode;
  /** Replaces a whole action row. */
  action?: (props: { action: NxFabAction }) => VNode;
  /** Replaces only the badge of an action. */
  badge?: (props: { action: NxFabAction }) => VNode;
}>();

const DRAG_THRESHOLD = 5;
/** Half of the 3.25rem (52px) main button — pointer tracks the button center. */
const FAB_MAIN_RADIUS_PX = 28;
/** Main button footprint used for viewport edge clamping. */
const FAB_MAIN_SIZE_PX = 64;
const VIEWPORT_MARGIN_PX = 8;

const hidden = computed(() => props.triggerMode === 'single' && props.actions.length === 0);

const hasMainBadge = computed(
  () => props.badge !== undefined && props.badge !== null && props.badge !== '' && props.badge !== 0
);
const mainLabel = computed(() =>
  hasMainBadge.value ? `${props.label} (${props.badge})` : props.label
);
if (import.meta.env.DEV && props.triggerMode === 'single' && props.actions.length === 0) {
  console.warn('[NxFab] triggerMode "single" needs at least one action; rendering nothing.');
}

function isClient(): boolean {
  return typeof window !== 'undefined';
}

/**
 * The dragged position as it is remembered: insets from the viewport's
 * bottom-right corner, so window resizes keep the FAB the same distance from
 * that edge (absolute top/left alone would leave it "stuck" upper-left when
 * growing).
 */
type FabInsets = {
  /** px from right edge of viewport to right edge of FAB */
  right: number;
  /** px from bottom edge of viewport to bottom edge of FAB */
  bottom: number;
};

function getViewportSize(): { width: number; height: number } {
  if (!isClient()) return { width: 0, height: 0 };
  return { width: window.innerWidth, height: window.innerHeight };
}

function insetsFromTopLeft(
  pos: { x: number; y: number },
  viewport: { width: number; height: number } = getViewportSize()
): { right: number; bottom: number } {
  return {
    right: viewport.width - pos.x - FAB_MAIN_SIZE_PX,
    bottom: viewport.height - pos.y - FAB_MAIN_SIZE_PX,
  };
}

function topLeftFromInsets(
  insets: { right: number; bottom: number },
  viewport: { width: number; height: number } = getViewportSize()
): { x: number; y: number } {
  return {
    x: viewport.width - FAB_MAIN_SIZE_PX - insets.right,
    y: viewport.height - FAB_MAIN_SIZE_PX - insets.bottom,
  };
}

/** Keep the FAB fully inside the viewport when using right/bottom insets. */
function clampInsets(
  insets: { right: number; bottom: number },
  viewport: { width: number; height: number } = getViewportSize()
): { right: number; bottom: number } {
  if (viewport.width <= 0 || viewport.height <= 0) return { ...insets };
  const maxRight = Math.max(
    VIEWPORT_MARGIN_PX,
    viewport.width - FAB_MAIN_SIZE_PX - VIEWPORT_MARGIN_PX
  );
  const maxBottom = Math.max(
    VIEWPORT_MARGIN_PX,
    viewport.height - FAB_MAIN_SIZE_PX - VIEWPORT_MARGIN_PX
  );
  return {
    right: clamp(insets.right, VIEWPORT_MARGIN_PX, maxRight),
    bottom: clamp(insets.bottom, VIEWPORT_MARGIN_PX, maxBottom),
  };
}

const pane = useNxPaneState<FabInsets>(() => props.storageKey);

const isInset = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/** The remembered position, or `null` when there is none. */
const stored = computed<FabInsets | null>(() => {
  const { right, bottom } = pane.state.value;
  return isInset(right) && isInset(bottom) ? { right, bottom } : null;
});

function writeStored(insets: FabInsets): void {
  pane.patch({ right: Math.round(insets.right), bottom: Math.round(insets.bottom) });
}

function clamp(v: number, min: number, max: number) {
  return Math.min(Math.max(v, min), max);
}

function clampDragPosition(pos: { x: number; y: number }): {
  x: number;
  y: number;
} {
  const { width, height } = getViewportSize();
  // Before layout (0×0 viewport) keep the raw stored coords so a reload does
  // not clamp everything into the corner and overwrite a good position later.
  if (width <= 0 || height <= 0) return { ...pos };
  return {
    x: clamp(pos.x, VIEWPORT_MARGIN_PX, Math.max(VIEWPORT_MARGIN_PX, width - FAB_MAIN_SIZE_PX)),
    y: clamp(pos.y, VIEWPORT_MARGIN_PX, Math.max(VIEWPORT_MARGIN_PX, height - FAB_MAIN_SIZE_PX)),
  };
}

/**
 * Live right/bottom insets while a custom position is active — restored at
 * setup, so the first paint already uses the saved spot (an onMounted-only
 * restore flashes the default corner after every reload).
 * Positioning uses CSS `right`/`bottom` so the browser keeps the distance to
 * the bottom-right corner on window resize without JS (until clamp needed).
 */
const edgeInsets = ref<FabInsets | null>(stored.value ? clampInsets(stored.value) : null);

const rootStyle = computed(() => {
  if (edgeInsets.value) {
    return {
      right: `${edgeInsets.value.right}px`,
      bottom: `${edgeInsets.value.bottom}px`,
      left: 'auto',
      top: 'auto',
    };
  }
  if (typeof props.position === 'object') {
    // Explicit {x,y} prop still uses top-left (rare / tests).
    return {
      left: `${props.position.x}px`,
      top: `${props.position.y}px`,
      right: 'auto',
      bottom: 'auto',
    };
  }
  return {};
});
const cornerClass = computed(() =>
  edgeInsets.value || typeof props.position === 'object' ? '' : `nx-fab--${props.position}`
);

/** The hover tag opens to the right only for a FAB in the left half of the screen. */
const hoverLabelAtEnd = computed(() => {
  const half = isClient() ? window.innerWidth / 2 : 0;
  if (edgeInsets.value) return half > 0 && edgeInsets.value.right > half;
  if (typeof props.position === 'object') return half > 0 && props.position.x < half;
  return props.position === 'bottom-left';
});

// ---- drag vs click (per-instance state) ----
let start: { x: number; y: number } | null = null;
let dragging = false;
let suppressClick = false;

function onPointerMove(e: Event) {
  const ev = e as PointerEvent;
  if (!start) return;
  if (!dragging && Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < DRAG_THRESHOLD) return;
  dragging = true;
  const topLeft = clampDragPosition({
    x: ev.clientX - FAB_MAIN_RADIUS_PX,
    y: ev.clientY - FAB_MAIN_RADIUS_PX,
  });
  // Store as right/bottom so resize keeps the same distance to the corner.
  edgeInsets.value = clampInsets(insetsFromTopLeft(topLeft));
}

function endPointerSession(cancelled = false) {
  if (isClient()) {
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', onPointerUp);
    document.removeEventListener('pointercancel', onPointerCancel);
  }
  if (dragging && edgeInsets.value && !cancelled) {
    suppressClick = true;
    writeStored(edgeInsets.value);
    emit('moved', topLeftFromInsets(edgeInsets.value));
  }
  start = null;
  dragging = false;
}

// A position remembered elsewhere (another device, a profile switch) or one
// that arrives after the mount (the settings answered) moves the FAB — never
// while the user drags it.
watch(stored, (next) => {
  if (!next || start) return;
  edgeInsets.value = clampInsets(next);
});

function onPointerUp() {
  endPointerSession();
}

function onPointerCancel() {
  suppressClick = false;
  endPointerSession(true);
}

function onPointerDown(e: PointerEvent) {
  if (!props.draggable) return;
  suppressClick = false;
  start = { x: e.clientX, y: e.clientY };
  dragging = false;
  if (isClient()) {
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', onPointerCancel);
  }
}

function onMainClick() {
  if (suppressClick) {
    suppressClick = false;
    return;
  }
  if (props.triggerMode === 'single') {
    emit('select', props.actions[0].key);
    return;
  }
  open.value = !open.value;
}

function onSelect(action: NxFabAction) {
  if (action.disabled) return;
  open.value = false;
  emit('select', action.key);
}

const mainEl = ref<HTMLButtonElement | null>(null);
const menuEl = ref<HTMLElement | null>(null);

// A4 menu-semantics lessons: Escape closes from ANY focus inside the FAB
// (main button or menuitem — keydown is bound on the root and bubbles up),
// and ArrowDown/ArrowUp rove focus across the enabled menuitems.
function onRootKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && open.value) {
    e.stopPropagation();
    open.value = false;
    mainEl.value?.focus();
    return;
  }
  if (
    (e.key === 'ArrowDown' || e.key === 'ArrowUp') &&
    open.value &&
    props.triggerMode === 'menu'
  ) {
    e.preventDefault();
    const items = Array.from(
      menuEl.value?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? []
    );
    if (items.length === 0) return;
    const idx = items.indexOf(document.activeElement as HTMLButtonElement);
    let next: number;
    if (idx === -1) {
      next = e.key === 'ArrowDown' ? 0 : items.length - 1;
    } else {
      next = (idx + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
    }
    items[next]?.focus();
  }
}

function onDocPointerDown(e: Event) {
  if (!open.value) return;
  const target = e.target;
  if (!(target instanceof Node)) return;
  const root = mainEl.value?.closest('.nx-fab');
  if (root && !root.contains(target)) open.value = false;
}

function onResize() {
  // CSS right/bottom already keep distance when the window grows. A smaller
  // viewport clamps what is shown; the remembered position stays the user's,
  // so the FAB returns to it when the window grows again.
  if (!edgeInsets.value) return;
  const clamped = clampInsets(stored.value ?? edgeInsets.value);
  if (clamped.right !== edgeInsets.value.right || clamped.bottom !== edgeInsets.value.bottom) {
    edgeInsets.value = clamped;
  }
}

onMounted(() => {
  // Re-resolve after layout has real viewport metrics (setup may run at 0×0).
  if (stored.value) edgeInsets.value = clampInsets(stored.value);
  if (isClient()) {
    document.addEventListener('pointerdown', onDocPointerDown);
    window.addEventListener('resize', onResize);
  }
});

onBeforeUnmount(() => {
  if (isClient()) {
    document.removeEventListener('pointerdown', onDocPointerDown);
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', onPointerUp);
    document.removeEventListener('pointercancel', onPointerCancel);
    window.removeEventListener('resize', onResize);
  }
});
</script>

<template>
  <div
    v-if="!hidden"
    class="nx-fab"
    :class="cornerClass"
    :style="rootStyle"
    @keydown="onRootKeydown"
  >
    <div
      v-if="triggerMode === 'menu' && open"
      ref="menuEl"
      class="nx-fab__menu"
      role="menu"
      :aria-label="label"
    >
      <button
        v-for="action in actions"
        :key="action.key"
        type="button"
        role="menuitem"
        class="nx-fab__item"
        :disabled="action.disabled"
        @click="onSelect(action)"
      >
        <slot name="action" :action="action">
          <span class="nx-fab__item-label">{{ action.label }}</span>
          <span class="nx-fab__item-btn">
            <component :is="action.icon" class="nxt-icon-lg" aria-hidden="true" />
            <slot name="badge" :action="action">
              <span v-if="action.badge !== undefined" class="nx-fab__badge">
                {{ action.badge }}
              </span>
            </slot>
          </span>
        </slot>
      </button>
    </div>
    <button
      ref="mainEl"
      type="button"
      class="nx-fab__main"
      :aria-label="mainLabel"
      :aria-haspopup="triggerMode === 'menu' ? 'menu' : undefined"
      :aria-expanded="triggerMode === 'menu' ? open : undefined"
      @pointerdown="onPointerDown"
      @click="onMainClick"
    >
      <slot name="icon">
        <component :is="icon ?? SparkleIcon" class="nxt-icon-lg" aria-hidden="true" />
      </slot>
      <span v-if="hasMainBadge" class="nx-fab__badge nx-fab__badge--main" aria-hidden="true">
        {{ badge }}
      </span>
    </button>
    <!-- Painted only: the button's aria-label already says it. Hidden while the
         speed-dial is open, whose rows carry their own labels. -->
    <span
      v-if="hoverLabel && !open"
      class="nx-fab__hover-label"
      :class="{ 'nx-fab__hover-label--end': hoverLabelAtEnd }"
      aria-hidden="true"
    >
      {{ label }}
    </span>
  </div>
</template>

<style scoped>
.nx-fab {
  position: fixed;
  z-index: var(--nxt-z-float);
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--nxt-space-sm);
}
.nx-fab--bottom-right {
  right: calc(var(--nxt-space-md) * 1.25);
  bottom: calc(var(--nxt-space-md) * 1.25);
}
.nx-fab--bottom-left {
  left: calc(var(--nxt-space-md) * 1.25);
  bottom: calc(var(--nxt-space-md) * 1.25);
  align-items: flex-start;
}
.nx-fab__main {
  position: relative;
  width: var(--nxt-control-size-xl);
  height: var(--nxt-control-size-xl);
  border-radius: var(--nxt-radius-full);
  border: none;
  background: var(--nxt-fab-bg);
  color: var(--nxt-fab-fg);
  box-shadow: var(--nxt-shadow-md);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  touch-action: none;
}
.nx-fab__main:hover {
  background: var(--nxt-fab-bg-hover);
}
.nx-fab__main:active {
  background: var(--nxt-fab-bg-active);
}
.nx-fab__main:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-fab-ring);
  outline-offset: calc(var(--nxt-size-unit) * 0.125);
}
/* Speed-dial opens upward; the label sits left of each round action. */
.nx-fab__menu {
  position: absolute;
  bottom: calc(100% + var(--nxt-space-sm));
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--nxt-space-xs);
}
.nx-fab--bottom-left .nx-fab__menu {
  left: 0;
  right: auto;
  align-items: flex-start;
}
.nx-fab__item {
  display: inline-flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
}
.nx-fab__item:disabled {
  opacity: 0.5;
  cursor: default;
}
.nx-fab__item-label {
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg);
  background: var(--nxt-surface-bg);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-sm);
  padding: calc(var(--nxt-space-2xs) / 2) var(--nxt-space-xs);
  box-shadow: var(--nxt-shadow-sm);
}
.nx-fab__item-btn {
  position: relative;
  width: var(--nxt-control-size-lg);
  height: var(--nxt-control-size-lg);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-surface-bg);
  border: var(--nxt-hairline) solid var(--nxt-border);
  box-shadow: var(--nxt-shadow-sm);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--nxt-fg);
}
.nx-fab__badge {
  position: absolute;
  top: calc(-1 * var(--nxt-space-2xs));
  right: calc(-1 * var(--nxt-space-2xs));
  min-width: calc(var(--nxt-size-unit) * 1);
  height: calc(var(--nxt-size-unit) * 1);
  padding: 0 var(--nxt-space-2xs);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-danger-bg);
  color: var(--nxt-danger-fg);
  font-size: calc(var(--nxt-font-size-xs) * 0.85);
  font-weight: var(--nxt-font-weight-bold);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
/* On the main button the badge gets a ring, so it does not melt into the
   FAB's own fill. */
.nx-fab__badge--main {
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-surface-bg);
}
/* Same chip as the speed-dial's row labels, beside the main button. */
.nx-fab__hover-label {
  position: absolute;
  top: 50%;
  right: calc(100% + var(--nxt-space-sm));
  transform: translateY(-50%);
  white-space: nowrap;
  pointer-events: none;
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg);
  background: var(--nxt-surface-bg);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-sm);
  padding: calc(var(--nxt-space-2xs) / 2) var(--nxt-space-xs);
  box-shadow: var(--nxt-shadow-sm);
  opacity: 0;
  visibility: hidden;
  transition:
    opacity var(--nxt-motion-fast) var(--nxt-ease-out),
    visibility var(--nxt-motion-fast);
}
.nx-fab__hover-label--end {
  right: auto;
  left: calc(100% + var(--nxt-space-sm));
}
.nx-fab__main:hover + .nx-fab__hover-label,
.nx-fab__main:focus-visible + .nx-fab__hover-label {
  opacity: 1;
  visibility: visible;
}
@media (prefers-reduced-motion: reduce) {
  .nx-fab__hover-label {
    transition: none;
  }
}
</style>
